import React, {useEffect} from 'react'
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import ErrorModalTS from '../../components/ErrorModal/ErrorModalTS'
import SpinnerTS from '../../components/Spinner/SpinnerTS'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useKeyDown, KeyTypeEnum } from '../../../../shared/providers/KeyDownProvider'
import { MONTHLY } from '../../lib/ComcastBilling.js'
import tts from '../../lib/TTS'
import useInAppDonationScreen from './hooks'
import type { DonationFrequency } from './types'
import passportImg from '../../assets/img/passportLogo.svg'

const FOCUS_KEYS = {
  SCREEN: 'in-app-donation',
  MONTHLY_BTN: 'donation-monthly-btn',
  ONETIME_BTN: 'donation-onetime-btn',
  GO_BACK: 'donation-go-back',
  WHAT_IS_PASSPORT: 'donation-what-is-passport',
}

function InAppDonationScreen() {
  const {
    stationName,
    preDonationCopy,
    monthlyAmount,
    oneTimeAmount,
    error,
    isLoading,
    executeDonationProcess,
    handleGoBack,
    handleWhatIsPassport,
  } = useInAppDonationScreen()

  const { ref, focusKey } = useHybridFocusable({
    focusKey: FOCUS_KEYS.SCREEN,
    focusable: true,
    trackChildren: true,
    isFocusBoundary: true,
    preferredChildFocusKey: FOCUS_KEYS.MONTHLY_BTN,
  })

  useKeyDown(KeyTypeEnum.Back, handleGoBack)

  useEffect(() => {
    tts.pushTextToRead(`Watch with ${stationName} Passport. `)
    tts.pushTextToRead(preDonationCopy)
    tts.addTextToRead(`$${monthlyAmount} monthly for recurring membership`)
    tts.enqueueTextToRead(' Your donation goes to your local station!')
    return () => tts.stopRead()
  }, [])

  const onFocusButton = (label: string) => {
    tts.stopRead()
    tts.addTextToRead(label)
  }

  const onPressMonthly = () => {
    executeDonationProcess({
      totalChargeAmount: monthlyAmount,
      donationFrequency: MONTHLY as DonationFrequency,
      donationType: 'monthly',
    })
  }

  const onPressOneTime = () => {
    executeDonationProcess({
      totalChargeAmount: oneTimeAmount,
      donationFrequency: 'one-time',
      donationType: 'one-time',
    })
  }

  if (error) {
    return (
      <ErrorModalTS
        title={error.errorTitle}
        description={error.errorText}
        error={error.e}
        buttons={error.buttons}
        hideStationLogo
      />
    )
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="in-app-donation">
        {isLoading && <SpinnerTS className="in-app-donation__spinner" />}

        <div className="in-app-donation__content">
          <h1 className="in-app-donation__title">
            Watch with {stationName}{' '}
            <img
              className="in-app-donation__passport-icon"
              src={passportImg}
              alt="Passport"
            />{' '}
            Passport
          </h1>
          <p className="in-app-donation__description">
            {preDonationCopy ? preDonationCopy : `Support ${stationName} and get extended, on-demand access to your favorite PBS shows and local programs as a member benefit. Enjoy new and classic PBS series by streaming early releases or binge-watching on any device with Passport.`}
          </p>

          <div className="in-app-donation__amounts">
            <FocusableButtonTS
              focusKey={FOCUS_KEYS.MONTHLY_BTN}
              onEnterPress={onPressMonthly}
              onFocus={() => onFocusButton(`$${monthlyAmount} monthly for recurring membership`)}
              className="in-app-donation__amount-btn"
              stealFocus
            >
              <span className="in-app-donation__amount-value"> ${monthlyAmount} monthly </span>
              <span className="in-app-donation__amount-label">
                for recurring membership
              </span>
            </FocusableButtonTS>

            <FocusableButtonTS
              focusKey={FOCUS_KEYS.ONETIME_BTN}
              onEnterPress={onPressOneTime}
              onFocus={() => onFocusButton(`$${oneTimeAmount} one-time for 1-year membership`)}
              className="in-app-donation__amount-btn"
            >
              <span className="in-app-donation__amount-value">
                ${oneTimeAmount} one-time
              </span>
              <span className="in-app-donation__amount-label">
                for 1-year membership
              </span>
            </FocusableButtonTS>
          </div>

          <p className="in-app-donation__local-note">
            <span className="in-app-donation__heart">❤</span> Your donation goes to your local station!
          </p>

          <div className="in-app-donation__actions">
            <FocusableButtonTS
              focusKey={FOCUS_KEYS.GO_BACK}
              onEnterPress={handleGoBack}
              onFocus={() => onFocusButton('Go Back')}
              className="in-app-donation__action-btn"
            >
              Go Back
            </FocusableButtonTS>

            <FocusableButtonTS
              focusKey={FOCUS_KEYS.WHAT_IS_PASSPORT}
              onEnterPress={handleWhatIsPassport}
              onFocus={() => onFocusButton('What is Passport?')}
              className="in-app-donation__action-btn"
            >
              What is Passport?
            </FocusableButtonTS>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default InAppDonationScreen
